import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { db } from '../firebase';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { findDuplicateClients } from '../services/clientDupes';

const STANDARD_SERVICES = [
  { key: 'Blog Optimization', label: 'Blog Optimization', note: 'Assign to specific day(s)' },
  { key: 'Social Posts', label: 'Social Posts', note: '2x per week' },
  { key: 'Video Generation', label: 'Video Generation', note: '1x per month' },
  { key: 'Press Release', label: 'Press Release', note: 'Last day of each month' },
  { key: 'Report Summary', label: 'Report Summary', note: 'First workday of each month' },
];

const DAYS = ['Su','M','T','W','Th','F','Sa'];

export default function ClientSetup() {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    businessName: '',
    contactName: '',
    contactEmail: '',
    contactPhone: '',
    clientUrl: '',
    services: [],
    blogDays: [],
    gptLink: '',
    driveLink: '',
    initialNote: '',
    wpApiKey: '',
    wpUsername: '',
    wpAppPassword: '',
    brandVoice: '',
    autoPublish: false,
    imageStyle: 'illustrated',
  });
  const [customProjects, setCustomProjects] = useState([]);
  const [newCustom, setNewCustom] = useState({ title: '', instructions: '', schedule: 'weekly', days: [] });
  const [showCustomForm, setShowCustomForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const toggleService = (key) => {
    setForm(f => ({
      ...f,
      services: f.services.includes(key)
        ? f.services.filter(s => s !== key)
        : [...f.services, key]
    }));
  };

  const toggleBlogDay = (day) => {
    setForm(f => ({
      ...f,
      blogDays: f.blogDays.includes(day)
        ? f.blogDays.filter(d => d !== day)
        : [...f.blogDays, day]
    }));
  };

  const addCustomProject = () => {
    if (!newCustom.title.trim()) return;
    setCustomProjects(p => [...p, { ...newCustom }]);
    setNewCustom({ title: '', instructions: '', schedule: 'weekly', days: [] });
    setShowCustomForm(false);
  };

  const handleSubmit = async () => {
    if (!form.businessName.trim()) { setError('Business name is required.'); return; }
    const dupes = await findDuplicateClients({ businessName: form.businessName, clientUrl: form.clientUrl }).catch(() => []);
    if (dupes.length && !window.confirm(
      `This client already exists:\n${dupes.map(d => `• ${d.businessName} (${d.isArchived ? 'archived' : 'active'})`).join('\n')}\n\n` +
      'A new record starts with EMPTY content data (brand kit, reports, reviews, etc.). ' +
      'To bring a returning client back, cancel and use Restore under All Clients → Archived.\n\nCreate a new record anyway?'
    )) return;
    setSaving(true);
    setError('');
    try {
      const clientRef = await addDoc(collection(db, 'clients'), {
        businessName: form.businessName,
        contactName: form.contactName,
        contactEmail: form.contactEmail,
        contactPhone: form.contactPhone,
        clientUrl: form.clientUrl,
        services: form.services,
        blogDays: form.blogDays,
        customProjects: customProjects,
        gptLink: form.gptLink,
        driveLink: form.driveLink,
        initialNote: form.initialNote,
        wpApiKey: form.wpApiKey,
        wpUsername: form.wpUsername,
        wpAppPassword: form.wpAppPassword,
        brandVoice: form.brandVoice,
        ...(form.brandVoice ? { brandVoiceUpdatedAt: serverTimestamp() } : {}),
        autoPublish: form.autoPublish,
        imageStyle: form.imageStyle,
        noContentQueue: !form.services.includes('Blog Optimization'),
        isArchived: false,
        createdAt: serverTimestamp(),
      });

      // Generate scheduled tasks for new client
      const now = new Date();
      const save = (taskType, dueDate) => addDoc(collection(db, 'tasks'), {
        clientId: clientRef.id, title: taskType, taskType, dueDate, completed: false, createdAt: serverTimestamp(),
      });

      if (form.services.includes('Social Posts')) {
        const end = new Date(now); end.setMonth(end.getMonth() + 6);
        let d = new Date(now); d.setHours(12, 0, 0, 0);
        while (d.getDay() !== 2) d.setDate(d.getDate() + 1);
        while (d <= end) { await save('Social Posts', new Date(d)); d = new Date(d); d.setDate(d.getDate() + 7); }
      }
      if (form.services.includes('Report Summary')) {
        for (let i = 0; i <= 6; i++) {
          const d = new Date(now.getFullYear(), now.getMonth() + i, 1, 12, 0, 0);
          if (d > now) await save('Report Summary', d);
        }
      }
      if (form.services.includes('Press Release')) {
        for (let i = 0; i <= 6; i++) {
          const d = new Date(now.getFullYear(), now.getMonth() + i + 1, 0, 12, 0, 0);
          if (d > now) await save('Press Release', d);
        }
      }
      if (form.services.includes('Video Generation')) {
        for (let i = 0; i <= 6; i++) {
          let d = new Date(now.getFullYear(), now.getMonth() + i, 1, 12, 0, 0);
          while (d.getDay() !== 5) d.setDate(d.getDate() + 1);
          d.setDate(d.getDate() + 7);
          if (d > now) await save('Video Generation', new Date(d));
        }
      }

      navigate('/clients');
    } catch (e) {
      setError('Failed to save client: ' + e.message);
    }
    setSaving(false);
  };

  return (
    <div className="page" style={{ maxWidth: 720 }}>
      <div className="card">
        <div className="card-title">Client Setup</div>
        {error && <div className="error-box" style={{ marginBottom: 16 }}>{error}</div>}

        <div className="form-field">
          <label>Client Business Name</label>
          <input type="text" value={form.businessName} onChange={e => setForm(f => ({ ...f, businessName: e.target.value }))} />
        </div>

        <div className="form-row" style={{ gridTemplateColumns: '1fr 1fr 1fr' }}>
          <div className="form-field">
            <label>Contact Name (Optional)</label>
            <input type="text" placeholder="e.g. Jane Doe" value={form.contactName} onChange={e => setForm(f => ({ ...f, contactName: e.target.value }))} />
          </div>
          <div className="form-field">
            <label>Contact Email (Optional)</label>
            <input type="email" placeholder="jane@example.com" value={form.contactEmail} onChange={e => setForm(f => ({ ...f, contactEmail: e.target.value }))} />
          </div>
          <div className="form-field">
            <label>Contact Phone (Optional)</label>
            <input type="tel" placeholder="e.g. (555) 555-5555" value={form.contactPhone} onChange={e => setForm(f => ({ ...f, contactPhone: e.target.value }))} />
          </div>
        </div>

        <div className="form-field">
          <label>Client's URL</label>
          <input type="url" placeholder="https://example.com" value={form.clientUrl} onChange={e => setForm(f => ({ ...f, clientUrl: e.target.value }))} />
        </div>

        <div className="services-box">
          <div className="services-box-title">Standard Services</div>
          {STANDARD_SERVICES.map(s => (
            <div key={s.key}>
              <label className="checkbox-row">
                <input type="checkbox" checked={form.services.includes(s.key)} onChange={() => toggleService(s.key)} />
                <span>{s.label}</span>
                <small>({s.note})</small>
              </label>
              {s.key === 'Blog Optimization' && form.services.includes('Blog Optimization') && (
                <div style={{ marginLeft: 24, marginTop: 6, marginBottom: 4 }}>
                  <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 6 }}>Select blog days:</div>
                  <div className="day-pills">
                    {DAYS.map(d => (
                      <div key={d} className={`day-pill ${form.blogDays.includes(d) ? 'active' : ''}`} onClick={() => toggleBlogDay(d)}>{d}</div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ))}

          <div style={{ marginTop: 16 }}>
            <div className="services-box-title">Custom Projects</div>
            {customProjects.map((p, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '6px 0', borderBottom: '1px solid var(--border)', fontSize: 13 }}>
                <span>{p.title} <span style={{ color: 'var(--text-muted)', fontSize: 11 }}>({p.schedule})</span></span>
                <button className="btn btn-sm btn-danger" onClick={() => setCustomProjects(ps => ps.filter((_, j) => j !== i))}>Remove</button>
              </div>
            ))}
            {showCustomForm ? (
              <div className="custom-project-add">
                <input type="text" placeholder="Project title" value={newCustom.title} onChange={e => setNewCustom(n => ({ ...n, title: e.target.value }))} />
                <textarea placeholder="Instructions / details" value={newCustom.instructions} onChange={e => setNewCustom(n => ({ ...n, instructions: e.target.value }))} style={{ minHeight: 60 }} />
                <div className="schedule-tabs">
                  {['weekly','monthly','one-time'].map(s => (
                    <button key={s} className={`schedule-tab ${newCustom.schedule === s ? 'active' : ''}`} onClick={() => setNewCustom(n => ({ ...n, schedule: s }))}>{s.charAt(0).toUpperCase() + s.slice(1)}</button>
                  ))}
                </div>
                {newCustom.schedule === 'weekly' && (
                  <div className="day-pills">
                    {DAYS.map(d => (
                      <div key={d} className={`day-pill ${newCustom.days.includes(d) ? 'active' : ''}`} onClick={() => setNewCustom(n => ({ ...n, days: n.days.includes(d) ? n.days.filter(x => x !== d) : [...n.days, d] }))}>{d}</div>
                    ))}
                  </div>
                )}
                <div style={{ display: 'flex', gap: 8 }}>
                  <button className="btn btn-primary btn-sm" onClick={addCustomProject}>Add</button>
                  <button className="btn btn-secondary btn-sm" onClick={() => setShowCustomForm(false)}>Cancel</button>
                </div>
              </div>
            ) : (
              <button className="custom-project-add-btn" onClick={() => setShowCustomForm(true)}>+ Add Custom Project</button>
            )}
          </div>
        </div>

        <div className="form-row">
          <div className="form-field">
            <label>Client Claude Project Link</label>
            <input type="url" value={form.gptLink} onChange={e => setForm(f => ({ ...f, gptLink: e.target.value }))} />
          </div>
          <div className="form-field">
            <label>Client Google Drive Link</label>
            <input type="url" value={form.driveLink} onChange={e => setForm(f => ({ ...f, driveLink: e.target.value }))} />
          </div>
        </div>

        <div className="form-field">
          <label>WordPress Publisher API Key</label>
          <input type="text" placeholder="c6pk_..." value={form.wpApiKey} onChange={e => setForm(f => ({ ...f, wpApiKey: e.target.value }))} style={{ fontFamily: 'monospace' }} />
        </div>

        <div className="form-field">
          <label>WP Username</label>
          <input type="text" placeholder="WordPress admin username" value={form.wpUsername} onChange={e => setForm(f => ({ ...f, wpUsername: e.target.value }))} style={{ fontFamily: 'monospace' }} />
        </div>

        <div className="form-field">
          <label>WP Application Password</label>
          <input type="password" placeholder="xxxx xxxx xxxx xxxx xxxx xxxx" value={form.wpAppPassword} onChange={e => setForm(f => ({ ...f, wpAppPassword: e.target.value }))} style={{ fontFamily: 'monospace' }} />
        </div>

        <div className="form-field">
          <label>Blog Image Style</label>
          <select value={form.imageStyle} onChange={e => setForm(f => ({ ...f, imageStyle: e.target.value }))}>
            <option value="illustrated">Realistic + Watercolor (Photo-real subject, painted surroundings)</option>
            <option value="realistic">Realistic (Professional photograph)</option>
            <option value="watercolor">Watercolor & Ink (Hand-painted, editorial illustration)</option>
          </select>
        </div>

        <div className="form-field">
          <label>Blog Publishing Mode</label>
          <div
            onClick={() => setForm(f => ({ ...f, autoPublish: !f.autoPublish }))}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 12, cursor: 'pointer', userSelect: 'none' }}
          >
            <div style={{ width: 44, height: 24, borderRadius: 12, background: form.autoPublish ? '#16a34a' : '#374151', position: 'relative', transition: 'background .2s' }}>
              <div style={{ position: 'absolute', top: 3, left: form.autoPublish ? 23 : 3, width: 18, height: 18, borderRadius: '50%', background: '#fff', transition: 'left .2s' }} />
            </div>
            <span style={{ fontSize: 13, color: 'var(--text-primary)' }}>
              {form.autoPublish ? 'Publish immediately when generated' : 'Save as draft for review'}
            </span>
          </div>
        </div>

        <div className="form-field">
          <label>Brand Voice Notes</label>
          <textarea
            placeholder={"Describe everything Claude needs to write for this client — their services, audience, locations served, tone, differentiators, and anything to always or never say in their content."}
            value={form.brandVoice}
            onChange={e => setForm(f => ({ ...f, brandVoice: e.target.value }))}
            style={{ minHeight: 80 }}
          />
        </div>

        <div className="form-field">
          <label>Initial Update / Instructions</label>
          <textarea
            placeholder="Add the first update or any important instructions for this client..."
            value={form.initialNote}
            onChange={e => setForm(f => ({ ...f, initialNote: e.target.value }))}
            style={{ minHeight: 100 }}
          />
        </div>

        <div className="btn-form-row">
          <button className="btn btn-secondary" onClick={() => navigate('/clients')}>Cancel</button>
          <button className="btn btn-primary" onClick={handleSubmit} disabled={saving}>
            {saving ? 'Saving...' : 'Add Client'}
          </button>
        </div>
      </div>
    </div>
  );
}
